import { convertTimeZone, dateTimeFormat } from '@magicdoorinc/date';
import { Popover, useLocalization } from '@magicdoorinc/ui';
import { createSignal, For, Show } from 'solid-js';
import { Portal } from 'solid-js/web';
import { useFeatureHighlights } from '~/contexts/global';
import { useIsMobileWidth } from '~/hooks/useIsMobileWidth';

const URL_REGEX = /(https?:\/\/[^\s]+)/g;

const renderDescription = (text: string) =>
  text.split(URL_REGEX).map((part) =>
    /^https?:\/\//.test(part) ? (
      <a href={part} target="_blank" rel="noreferrer" class="text-primary underline hover-allowed:hover:opacity-80">
        {part}
      </a>
    ) : (
      part
    )
  );

export const WhatsNewButton = () => {
  const { t } = useLocalization();
  const { highlights, pulse } = useFeatureHighlights();
  const isMobileWidth = useIsMobileWidth();
  const [isOpen, setIsOpen] = createSignal(false);

  const items = () => highlights()?.items ?? [];
  const hasItems = () => items().length > 0;

  return (
    <Show when={hasItems()}>
      <Show when={isOpen() && isMobileWidth()}>
        <Portal>
          <div class="fixed inset-0 z-40 bg-black/70" onClick={() => setIsOpen(false)} />
        </Portal>
      </Show>
      <Popover open={isOpen()} onOpenChange={setIsOpen}>
        <Popover.Trigger class="relative flex h-[38px] items-center justify-center rounded-full border border-separator px-2.5 text-text-02 transition-colors aria-expanded:border-primary aria-expanded:text-primary hover-allowed:hover:bg-light-pink hover-allowed:hover:text-primary">
          <svg class="size-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="1.5">
            <path
              stroke-linecap="round"
              stroke-linejoin="round"
              d="M21 11.25v8.25a1.5 1.5 0 0 1-1.5 1.5H5.25a1.5 1.5 0 0 1-1.5-1.5v-8.25M12 4.875A2.625 2.625 0 1 0 9.375 7.5H12m0-2.625V7.5m0-2.625A2.625 2.625 0 1 1 14.625 7.5H12m0 0V21m-8.625-9.75h18c.621 0 1.125-.504 1.125-1.125v-1.5c0-.621-.504-1.125-1.125-1.125h-18c-.621 0-1.125.504-1.125 1.125v1.5c0 .621.504 1.125 1.125 1.125Z"
            />
          </svg>
          <span class="absolute right-1.5 top-1.5 flex size-2">
            <Show when={pulse()}>
              <span class="absolute -inset-0.5 animate-[ping_1.6s_ease-out_infinite] rounded-full bg-orange-400 opacity-75" />
            </Show>
            <span class="relative inline-flex size-2 rounded-full bg-orange-500" />
          </span>
        </Popover.Trigger>
        <Popover.Content
          as="div"
          align="end"
          class="my-1 w-[calc(100vw-1rem)] overflow-hidden rounded-md border border-separator bg-white shadow-lg md:w-96">
          <div class="border-b border-separator px-4 py-3">
            <h3 class="text-base font-semibold text-text-01">{t("What's new")}</h3>
          </div>
          <ul class="max-h-[26rem] space-y-3 overflow-y-auto bg-grey/50 p-3">
            <For each={items()}>
              {(item) => (
                <li class="rounded-md border border-separator bg-white p-3">
                  <p class="break-words text-base font-semibold text-text-01">{item.title}</p>
                  <p class="mt-0.5 text-xs text-text-02">{dateTimeFormat(convertTimeZone(item.startDate), { timeFormat: false })}</p>
                  <div class="my-2 border-t border-separator" />
                  <p class="max-h-40 overflow-y-auto whitespace-pre-line break-words text-sm text-text-02">
                    {renderDescription(item.description)}
                  </p>
                </li>
              )}
            </For>
          </ul>
        </Popover.Content>
      </Popover>
    </Show>
  );
};
